import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Badge } from 'components/badges';
import { Button, PrimaryButton } from 'components/buttons';
import { SearchInput, ToggleField } from 'components/forms';
import { AlertError, AlertInfo, AlertWarning } from 'components/layout/alerts';
import { Skeleton } from 'components/layout/loading';
import {
  setReminderTodo,
  clearError,
  fetchReminders,
  selectRemindersError,
} from 'store/slices/remindersSlice';
import {
  fetchTodos,
  selectAllTodos,
  selectTodosLoading,
} from 'store/slices/todosSlice';
import {
  TODO_PRIORITY_BADGE_COLORS,
  TODO_PRIORITY_LABELS,
  TODO_STATUS_LABELS,
  TODO_STATUS,
} from 'constants/todos';
import { REMINDER_TYPES } from 'constants/reminders';
import { formatSchedule } from 'utils/date';
import { formatReminderActiveWindow } from 'utils/reminders';

function TodoOptionListItem({ todo, isCurrent, isSelected, onSelect }) {
  const priorityColor = TODO_PRIORITY_BADGE_COLORS[todo.priority] || 'gray';
  const priorityLabel = TODO_PRIORITY_LABELS[todo.priority] || todo.priority;
  const statusLabel = TODO_STATUS_LABELS[todo.status] || todo.status;

  return (
    <div
      className={classNames(
        'flex items-start rounded-md border p-4 transition-colors cursor-pointer',
        {
          'border-indigo-500 bg-indigo-900/20': isSelected,
          'border-gray-700 bg-gray-800 hover:bg-gray-700': !isSelected,
        }
      )}
      onClick={onSelect}
    >
      <div
        className={classNames(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          {
            'border-indigo-500': isSelected,
            'border-gray-500': !isSelected,
          }
        )}
      >
        {isSelected && <div className="h-2 w-2 rounded-full bg-indigo-500" />}
      </div>

      <div className="ml-3 min-w-0 flex-auto">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-white">{todo.name}</p>
          <Badge size="sm" color={priorityColor}>{priorityLabel}</Badge>
          <Badge size="sm" color="gray">{statusLabel}</Badge>
          {isCurrent && <Badge size="sm" color="blue">Current todo</Badge>}
        </div>

        {todo.tags && todo.tags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {todo.tags.map((tag) => (
              <Badge key={tag._id || tag} size="sm" color="gray">
                {typeof tag === 'object' ? tag.name : tag}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AssignTodoToReminder({
  reminder,
  onSuccess,
  onCancel,
  cancelLabel = 'Cancel',
  successLabel,
  className = '',
}) {
  const dispatch = useDispatch();
  const allTodos = useSelector(selectAllTodos);
  const loadingTodos = useSelector(selectTodosLoading);
  const error = useSelector(selectRemindersError);
  const user = useSelector((state) => state.auth.user);
  const isInitialLoadingTodos = loadingTodos && allTodos.length === 0;

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTodoId, setSelectedTodoId] = useState(null);
  const [todoControlsReminder, setTodoControlsReminder] = useState(
    Boolean(reminder?.todoControlsReminder)
  );
  const [isAttaching, setIsAttaching] = useState(false);

  useEffect(() => {
    dispatch(clearError());
    dispatch(fetchTodos());
  }, [dispatch]);

  const reminderId = reminder?._id ?? null;
  const isOneTime = reminder?.type === REMINDER_TYPES.ONE_TIME;
  const currentTodo = reminder?.todo ?? null;
  const currentTodoId = currentTodo?._id ?? null;
  const currentTodoLabel = currentTodo?.name || (currentTodoId ? 'Unknown todo' : 'Not assigned');
  const selectedTodo = allTodos.find((t) => t._id === selectedTodoId) || null;
  const scheduleText = formatSchedule(reminder, user?.settings, true);
  const activeWindowText = formatReminderActiveWindow(reminder, user?.settings);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredTodos = allTodos.filter((todo) => {
    if (todo.status !== TODO_STATUS.INPROGRESS) return false;
    if (!normalizedSearchTerm) return true;
    return String(todo.name || '').toLowerCase().includes(normalizedSearchTerm);
  });

  const replacementWarning = currentTodoId && selectedTodoId && currentTodoId !== selectedTodoId
    ? `This reminder already has todo "${currentTodoLabel}" assigned. Attaching "${selectedTodo?.name}" will replace it.`
    : null;

  const handleAttach = async () => {
    if (!reminderId || !selectedTodoId) return;
    try {
      setIsAttaching(true);
      await dispatch(setReminderTodo({
        reminderId,
        todoId: selectedTodoId,
        todoControlsReminder: isOneTime ? false : todoControlsReminder,
      })).unwrap();

      dispatch(fetchReminders());

      if (onSuccess) onSuccess();
    } catch (_error) {
      // error is captured in Redux state via rejectWithValue and displayed in the UI
    } finally {
      setIsAttaching(false);
    }
  };

  return (
    <div className={`flex h-full flex-col ${className}`}>
      {error && (
        <div className="mb-4">
          <AlertError title="Error">{error}</AlertError>
        </div>
      )}

      <div className="mb-4 rounded-lg border border-gray-700 bg-gray-800 p-4">
        <h3 className="text-sm font-medium text-gray-300">Selected Reminder</h3>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <p className="font-semibold text-white">{reminder.subject}</p>
          {currentTodoId && <Badge size="sm" color="purple">Todo assigned</Badge>}
        </div>
        <p className="mt-2 text-sm text-gray-300">{scheduleText}</p>
        {activeWindowText && <p className="mt-1 text-xs text-gray-400">{activeWindowText}</p>}
        <p className="mt-1 text-xs text-gray-400">Current todo: {currentTodoLabel}</p>
      </div>

      <div className="mb-4">
        <SearchInput
          id="assign-todo-search"
          label="Search todos"
          placeholder="Search todos..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          disabled={isAttaching}
          loading={isInitialLoadingTodos}
          className="w-full"
        />
      </div>

      <div className="custom-scrollbar min-h-[200px] max-h-[400px] flex-1 space-y-4 overflow-y-auto pr-2">
        {isInitialLoadingTodos && !isAttaching ? (
          <>
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
          </>
        ) : (
          <>
            {filteredTodos.map((todo) => (
              <TodoOptionListItem
                key={todo._id}
                todo={todo}
                isCurrent={todo._id === currentTodoId}
                isSelected={todo._id === selectedTodoId}
                onSelect={() => setSelectedTodoId(todo._id)}
              />
            ))}

            {filteredTodos.length === 0 && (
              <p className="py-8 text-center text-gray-400">
                {searchTerm ? 'No matching in-progress todos found.' : 'No in-progress todos found.'}
              </p>
            )}
          </>
        )}
      </div>

      {replacementWarning && (
        <div className="mt-4">
          <AlertWarning>{replacementWarning}</AlertWarning>
        </div>
      )}

      <div className="mt-4">
        <ToggleField
          id="todo-controls-reminder"
          label="Todo controls reminder status"
          checked={isOneTime ? false : todoControlsReminder}
          onChange={setTodoControlsReminder}
          disabled={isAttaching || isOneTime}
          loading={isInitialLoadingTodos}
        />
        {isOneTime && (
          <p className="mt-1.5 text-xs text-gray-500">
            Not available — one-time reminders complete automatically after firing.
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-y-2">
        <PrimaryButton
          onClick={handleAttach}
          disabled={!selectedTodoId || isInitialLoadingTodos || isAttaching}
          className="w-full"
        >
          {isAttaching
            ? 'Attaching...'
            : (successLabel || (selectedTodoId ? 'Attach & Finish' : 'Finish'))}
        </PrimaryButton>

        {onCancel && (
          <Button
            onClick={onCancel}
            className="w-full"
            disabled={isAttaching}
          >
            {cancelLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
